import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { 
  BarChart3, 
  TrendingUp, 
  Download, 
  FileText, 
  Calendar,
  Eye,
  CheckCircle
} from 'lucide-react';

interface FormUsageStats {
  form_code: string;
  form_name: string;
  total_downloads: number;
  total_views: number;
  current_version: string;
  last_updated: string;
}

interface DownloadTrend {
  date: string;
  downloads: number;
}

interface FormAnalyticsProps {
  formCode?: string;
}

export default function FormAnalytics({ formCode }: FormAnalyticsProps) {
  const [stats, setStats] = useState<FormUsageStats[]>([]);
  const [trends, setTrends] = useState<DownloadTrend[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    loadAnalytics();
  }, [formCode, dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Load form usage stats
      const { data: usageData, error: usageError } = await supabase
        .from('form_usage_stats')
        .select('*')
        .eq(formCode ? 'form_code' : 'id', formCode || null)
        .order('total_downloads', { ascending: false });

      if (usageError) throw usageError;
      setStats(usageData || []);

      // Load download trends
      let daysAgo = 30;
      if (dateRange === '7d') daysAgo = 7;
      if (dateRange === '90d') daysAgo = 90;
      if (dateRange === 'all') daysAgo = 365;

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      const { data: trendData, error: trendError } = await supabase
        .from('form_download_log')
        .select('downloaded_at')
        .gte('downloaded_at', startDate.toISOString())
        .eq(formCode ? 'form_code' : 'id', formCode || null);

      if (trendError) throw trendError;

      // Group by date
      const groupedByDate: Record<string, number> = {};
      (trendData || []).forEach((record) => {
        const date = new Date(record.downloaded_at).toLocaleDateString();
        groupedByDate[date] = (groupedByDate[date] || 0) + 1;
      });

      const trendsArray = Object.entries(groupedByDate).map(([date, downloads]) => ({
        date,
        downloads,
      }));
      setTrends(trendsArray);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalDownloads = stats.reduce((sum, stat) => sum + stat.total_downloads, 0);
  const totalViews = stats.reduce((sum, stat) => sum + stat.total_views, 0);
  const avgDownloadsPerForm = stats.length > 0 ? Math.round(totalDownloads / stats.length) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Downloads</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalDownloads.toLocaleString()}</p>
            </div>
            <Download className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Views</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalViews.toLocaleString()}</p>
            </div>
            <Eye className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Forms</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{stats.length}</p>
            </div>
            <FileText className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg per Form</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{avgDownloadsPerForm}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Time Period:</span>
          <div className="flex gap-2">
            {(['7d', '30d', '90d', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  dateRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range === '7d' ? 'Last 7 Days' : 
                 range === '30d' ? 'Last 30 Days' :
                 range === '90d' ? 'Last 90 Days' : 'All Time'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Download Trends Chart */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Download Trends
        </h3>
        {trends.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No download data for this period</p>
        ) : (
          <div className="space-y-2">
            {trends.slice(0, 10).map((trend) => {
              const maxDownloads = Math.max(...trends.map(t => t.downloads));
              const barWidth = (trend.downloads / maxDownloads) * 100;
              
              return (
                <div key={trend.date} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-gray-600">{trend.date}</div>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
                    <div 
                      className="bg-blue-600 h-6 rounded-full flex items-center justify-end pr-2 transition-all duration-300"
                      style={{ width: `${barWidth}%` }}
                    >
                      <span className="text-xs font-medium text-white">
                        {trend.downloads}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top Forms Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Form Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Form
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Views
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Downloads
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversion Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No usage data available
                  </td>
                </tr>
              ) : (
                stats.map((stat) => {
                  const conversionRate = stat.total_views > 0 
                    ? ((stat.total_downloads / stat.total_views) * 100).toFixed(1)
                    : '0';
                  
                  return (
                    <tr key={stat.form_code} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <div>
                            <p className="font-medium text-gray-900">{stat.form_code}</p>
                            <p className="text-sm text-gray-500">{stat.form_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                          <CheckCircle className="w-3 h-3" />
                          v{stat.current_version}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {stat.total_views.toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Download className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900">
                            {stat.total_downloads.toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${
                            parseFloat(conversionRate) >= 50 ? 'text-green-600' :
                            parseFloat(conversionRate) >= 25 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {conversionRate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {new Date(stat.last_updated).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Analytics</h3>
        <div className="flex gap-3">
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
            Export as CSV
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
            <FileText className="w-4 h-4" />
            Export as PDF
          </button>
        </div>
      </div>
    </div>
  );
}
